import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TopBar from '../../components/TopBar'
import { useAnalytics } from '../../context/AnalyticsContext'
import { getOnboarding, setOnboarding } from './onboardingStorage'
import './photos.css'

const compressImage = (file, maxWidth = 1280, maxHeight = 1280, quality = 0.82) =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        let { width, height } = img
        if (width > height && width > maxWidth) {
          height = (height * maxWidth) / width
          width = maxWidth
        } else if (height >= width && height > maxHeight) {
          width = (width * maxHeight) / height
          height = maxHeight
        }
        canvas.width = Math.round(width)
        canvas.height = Math.round(height)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
        resolve(canvas.toDataURL('image/jpeg', quality))
      }
      img.onerror = () => resolve(e.target.result)
      img.src = e.target.result
    }
    reader.onerror = () => resolve(null)
    reader.readAsDataURL(file)
  })

const OnboardingPhotos = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const saved = getOnboarding()

  const [photos, setPhotos] = useState(() => {
    const existing = Array.isArray(saved.photos) ? saved.photos.slice(0, 3) : []
    return [existing[0] || null, existing[1] || null, existing[2] || null]
  })
  const [error, setError] = useState('')
  const refs = [useRef(null), useRef(null), useRef(null)]

  const onBack = () => navigate('/onboarding/teaser')

  const uploadAt = (idx) => refs[idx].current?.click()

  const onFile = async (idx, file) => {
    if (!file) return
    const dataUrl = await compressImage(file)
    if (!dataUrl) return
    const next = [...photos]
    next[idx] = dataUrl
    setPhotos(next)
    setOnboarding({ photos: next.filter(Boolean) })
    if (error) setError('')
    if (refs[idx].current) refs[idx].current.value = ''
  }

  const removeAt = (idx) => {
    const next = [...photos]
    next[idx] = null
    setPhotos(next)
    setOnboarding({ photos: next.filter(Boolean) })
  }

  const onContinue = () => {
    const count = photos.filter(Boolean).length
    if (count < 1) {
      setError('Please upload at least one photo.')
      return
    }
    setError('')
    trackEvent('funnel_photos_completed', { step: 8, count })
    navigate('/onboarding/email')
  }

  return (
    <div className="onboarding-page">
      <TopBar currentStep={8} totalSteps={10} onBack={onBack} showBack={true} />
      <div className="onboarding-content">
        <h1 className="h1">{t('onboarding.photosAddPhotos')}</h1>
        <p className="p">{t('onboarding.photosUploadOnePhoto')}</p>

        <div className={`photo-row ${error ? 'error' : ''}`}>
          {photos.map((p, idx) => (
            <div key={idx} className="photo-card" onClick={() => uploadAt(idx)} role="button" tabIndex={0}>
              <input
                ref={refs[idx]}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => onFile(idx, e.target.files?.[0])}
              />
              {p ? <img className="photo-img" src={p} alt={`Photo ${idx + 1}`} /> : <div className="photo-plus">+</div>}
              {p ? (
                <button className="photo-remove" onClick={(e) => (e.stopPropagation(), removeAt(idx))} aria-label={t('onboarding.photosRemovePhoto')}>
                  ✕
                </button>
              ) : null}
            </div>
          ))}
        </div>
        {error ? <div className="error-text">{error}</div> : null}
      </div>

      <button className="cta-fixed" onClick={onContinue}>
        {t('common.continue')}
      </button>
    </div>
  )
}

export default OnboardingPhotos

